import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useMonarch, useMonarchRequest } from "@redventures/cohesion-utils-react";
import { OfferMerchMetadata } from "~/types/offerObject";
import { setOfferMerchData } from "~/redux/actions/common";

interface MonarchRuleDataWrapper {
  data?: OfferMerchMetadata;
  isLoading: boolean;
}

/**
 * Hook to get global offer merchandising data from Monarch rule
 * Uses useMonarchRule to fetch data and dispatches it to Redux for backward compatibility
 * Falls back to an independent useMonarchRequest when the bulk evaluation errors
 */
export const useGlobalOfferMerchData = (): MonarchRuleDataWrapper => {
  const dispatch = useDispatch();

  const { result, isLoading, err } = useMonarch();
  const ruleData = (result?.source?.cartOfferMerchandisingNgcBau?.value ?? {}) as { data?: OfferMerchMetadata };

  // Independent retry: fires only when the bulk Monarch evaluation errors
  const { value: fallbackValue, isLoading: fallbackLoading } = useMonarchRequest<{ data?: OfferMerchMetadata }>(
    "rule",
    "cartOfferMerchandisingNgcBau",
    {
      skip: !err,
    }
  );

  const resolvedData = ruleData?.data ?? fallbackValue?.data;
  const resolvedLoading = isLoading || (!!err && fallbackLoading);

  const dataObj = useMemo<MonarchRuleDataWrapper>(
    () => ({
      data: resolvedData,
      isLoading: resolvedLoading,
    }),
    [resolvedData, resolvedLoading]
  );

  // Dispatch data to Redux for other hooks to consume (backward compatibility)
  // Only update Redux if data exists and is not empty
  useEffect(() => {
    const hasData = dataObj.data && Object.keys(dataObj.data).length > 0;
    dispatch(setOfferMerchData(hasData ? dataObj.data : null));
  }, [dataObj.data, dispatch]);

  return dataObj;
};

export default useGlobalOfferMerchData;
